/**
 * Wörterbuch der Oberfläche (Klartext, Du-Form, Plan 9.2 „Wörterbuch“, [A] 5.1).
 *
 * Regeln (durch `de.test.ts` erzwungen): kein verbotener Begriff aus `forbidden-terms.ts`, keine Sie-Form,
 * keine Versalien-Etiketten, keine Pfeile an Linktexten, keine mit Mittelpunkt verketteten Meta-Zeilen.
 * Platzhalter stehen in geschweiften Klammern (`{date}`); `t()` prüft Schlüssel und Platzhalter zur
 * Übersetzungszeit. Knopftext nennt das Ergebnis („Auf LinkedIn öffnen“, nie „Senden“).
 */
export const de = {
  app: {
    name: 'Socialfy',
    titleSuffix: ' – Socialfy',
    loading: 'Wird geladen',
  },

  nav: {
    label: 'Hauptnavigation',
    labelMobile: 'Navigation unten',
    skipToContent: 'Zum Inhalt springen',
    heute: 'Heute',
    nachrichten: 'Nachrichten',
    menschen: 'Menschen',
    netzwerk: 'Netzwerk',
    zielpersonen: 'Zielpersonen',
    zielpersonenFinden: 'Zielpersonen finden',
    sichtbarkeit: 'Sichtbarkeit',
    mehr: 'Mehr',
    zielgruppen: 'Zielgruppen',
    ziele: 'Ziele',
    ablaeufe: 'Abläufe',
    einwilligung: 'Einwilligung',
    konto: 'Konto',
    einstellungen: 'Einstellungen',
    datenschutz: 'Datenschutz',
    verbindungen: 'Verbindungen',
    demoStart: 'Beispieldaten ansehen',
    demoEnd: 'Zurück zu deinen Daten',
    logout: 'Abmelden',
  },

  pages: {
    heute: 'Heute',
    menschen: 'Menschen',
    person: 'Person',
    gespraech: 'Gespräch',
    netzwerk: 'Netzwerk',
    konto: 'Konto',
    einstellungen: 'Einstellungen',
    freigaben: 'Freigaben',
    datenschutz: 'Datenschutz',
    verbindungen: 'Verbindungen',
    freigabe: 'Freigabe',
    zielpersonen: 'Zielpersonen',
    zielperson: 'Zielperson',
    neueZielpersonen: 'Neue Zielpersonen finden',
    nachrichten: 'Nachrichten',
    nachricht: 'Gespräch',
    zielgruppen: 'Zielgruppen',
    zielgruppe: 'Zielgruppe',
    ziele: 'Ziele',
    ablaeufe: 'Abläufe',
    einwilligung: 'Einwilligung',
    sichtbarkeit: 'Sichtbarkeit',
    werkstatt: 'Werkstatt',
    themen: 'Themen',
    kommentieren: 'Kommentieren',
    fragen: 'Fragen',
    stimme: 'Stimme',
    regeln: 'Regeln',
    fakten: 'Fakten',
    plan: 'Plan',
    wirkung: 'Wirkung',
    zustaende: 'Zustände',
    kein_zugang: 'Kein Zugang',
    nicht_gefunden: 'Seite nicht gefunden',
  },

  theme: {
    legend: 'Farbvariante',
    light: 'Hell',
    dark: 'Dunkel',
    system: 'Wie System',
  },

  demo: {
    region: 'Demo-Modus',
    banner: 'Du siehst Beispieldaten.',
    back: 'Zurück zu deinen Daten',
    notPossible: 'Im Demo-Modus nicht möglich.',
    nothingOpens: 'Im Demo-Modus wird nichts geöffnet.',
    switching: 'Wir wechseln die Daten.',
  },

  session: {
    loading: 'Dein Konto wird geprüft.',
    loadFailed: 'Wir konnten dein Konto nicht prüfen.',
    signedOut: 'Du wirst zur Anmeldung weitergeleitet.',
  },

  states: {
    loading: 'Wird geladen',
    slow: 'Dauert länger als üblich.',
    refreshFailed: 'Die Daten konnten nicht aktualisiert werden. Du siehst den letzten Stand.',
    errorTitle: 'Das konnten wir gerade nicht laden.',
    retry: 'Erneut versuchen',
    requestId: 'Fehlernummer: {requestId}',
    dataGap: 'Achtung: Daten seit {date} unvollständig.',
    offline: 'Keine Verbindung – du siehst den letzten Stand von {time}.',
    offlineNoData: 'Keine Verbindung – Änderungen sind gerade nicht möglich.',
    noAccessTitle: 'Kein Zugang',
    noAccessText: 'Dieses Konto ist nicht freigeschaltet.',
    noAccessAction: 'Mit einem anderen Konto anmelden',
    notFoundTitle: 'Seite nicht gefunden',
    notFoundText: 'Diese Seite gibt es nicht.',
    toStart: 'Zur Startseite',
    comingSoon: 'Diese Seite folgt bald.',
    routeErrorTitle: 'Etwas ist schiefgelaufen',
    routeErrorText:
      'Die Seite konnte nicht angezeigt werden. Lade sie neu; falls das nicht hilft, versuche es später noch einmal.',
    reloadPage: 'Seite neu laden',
    gallery: {
      loading: 'Laden',
      empty: 'Leer',
      emptyMessage: 'Du hast noch keine Ziele.',
      emptyAction: 'Erstes Ziel anlegen',
      error: 'Fehler',
      gap: 'Datenlücke',
      offline: 'Offline',
      offlineHint: 'Trenne die Verbindung, um die Leiste zu sehen.',
      dialog: 'Dialog',
      dialogOpen: 'Beispiel-Dialog öffnen',
      dialogTitle: 'Beispiel wirklich löschen?',
      dialogText: 'Das ist nur ein Beispiel und löscht nichts.',
      dialogConfirm: 'Beispiel löschen',
    },
  },

  ui: {
    close: 'Schließen',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    undo: 'Rückgängig',
    undoAll: 'Alle {count} rückgängig',
    toastRepeated: '{message} ({count}-mal)',
    hintShow: 'Erklärung anzeigen',
    howCounted: 'Wie wird das gezählt?',
    progressOf: '{current} von {total}',
    progressExpected: 'So weit solltest du heute sein: {expected}',
    typeToConfirm: 'Tippe {word} ein, um zu bestätigen.',
    typeToConfirmLabel: 'Bestätigungswort',
    notifications: 'Hinweise',
    disabledBecause: 'Nicht möglich: {reason}',
    saving: 'Wird gespeichert …',
    skeletonLabel: 'Inhalt wird geladen',
    trendUp: 'gestiegen',
    trendDown: 'gesunken',
    trendFlat: 'gleich geblieben',
    trendNew: 'neu',
    trendNone: 'noch nicht vergleichbar',
    statusGood: 'Gut',
    statusNotice: 'Hinweis',
    statusSerious: 'Achtung',
    statusCritical: 'Kritisch',
    tableEmpty: 'Keine Einträge.',
  },

  links: {
    openLinkedIn: 'Auf LinkedIn öffnen',
    copyAndOpen: 'Text kopieren und auf LinkedIn öffnen',
    copied: 'Text kopiert.',
    copyFailed: 'Kopieren hat nicht geklappt. Markiere den Text und kopiere ihn selbst.',
    copyFallbackLabel: 'Text zum Kopieren',
    preparing: 'Wird vorbereitet',
    previewLabel: 'So lautet der Text',
    popupBlocked: 'LinkedIn hat sich nicht geöffnet. Öffne es mit diesem Link.',
    addressNotOpened:
      'Die Adresse wurde nicht geöffnet. Öffne LinkedIn selbst und füge den Text dort ein.',
    opensNewTab: 'öffnet in einem neuen Tab',
  },

  charts: {
    asTable: 'Als Tabelle',
    asChart: 'Als Diagramm',
    legend: 'Legende',
    tableFor: 'Tabelle zu: {title}',
    summary: '{title}. {summary}',
    noData: 'Keine Daten für dieses Diagramm.',
    failed: 'Das Diagramm konnte nicht geladen werden. Die Werte stehen in der Tabelle.',
    loading: 'Diagramm wird geladen',
  },

  terms: {
    linkedInAccess: 'Zugang zu LinkedIn',
    liveUpdate: 'laufende Aktualisierung',
    fullSync: 'vollständiger Abgleich',
    dataGap: 'Lücke in den Daten',
    strength: 'Wie eng',
    basis: 'Einschätzung beruht auf {count} von 5 Anhaltspunkten',
    identityGuess: 'Vermutlich dieselbe Person – bitte bestätigen',
  },

  strength: {
    fleeting: 'flüchtig',
    loose: 'locker',
    familiar: 'vertraut',
    close: 'eng',
    unrated: 'noch nicht einschätzbar',
  },

  connection: {
    connected: 'verbunden',
    invited: 'Anfrage gesendet',
    invited_by: 'Anfrage erhalten',
    none: 'nicht verbunden',
    unknown: 'unbekannt',
  },

  goalLight: {
    achieved: 'Erreicht',
    missed: 'Verfehlt',
    too_early: 'Noch zu früh',
    on_track: 'Im Plan',
    behind: 'Hinkt hinterher',
    off_track: 'Weit hinter Plan',
  },

  dataGapReason: {
    saturated: 'LinkedIn hat zu viele Ereignisse gleichzeitig gemeldet; ein Teil fehlt.',
    lookback_exceeded: 'Die Aktualisierung war länger als 28 Tage unterbrochen.',
    snapshot_incomplete: 'Der vollständige Abgleich war unvollständig.',
    possible_unordered: 'Die Reihenfolge einiger Ereignisse ist unsicher.',
    unknown: 'Der Grund ist nicht bekannt.',
  },

  reminder: {
    dismiss: 'Ausblenden',
    loadFailed: 'Die Hinweise konnten nicht geladen werden.',
    view: 'Ansehen',
    renew: 'Jetzt erneuern',
    unknown: 'Es gibt einen Hinweis zu deinem Konto.',
    token_expiring: 'Der Zugang zu LinkedIn läuft {when} ab.',
    token_expired: 'Der Zugang zu LinkedIn ist abgelaufen.',
    token_issue_unknown:
      'Wir wissen nicht, wann der Zugang zu LinkedIn abläuft. Trage das Datum nach.',
    auth_failed: 'LinkedIn hat den Zugang abgelehnt. Bitte erstelle einen neuen.',
    consent_expiring: 'Deine Einwilligung für den Zugang zu LinkedIn läuft {when} ab.',
    consent_revoked:
      'Die Einwilligung für den Zugang zu LinkedIn wurde widerrufen. Die Aktualisierung ist pausiert.',
    account_mismatch:
      'Der Zugang gehört zu einem anderen LinkedIn-Konto als deinem. Die Aktualisierung ist pausiert.',
    unbound_token:
      'Der Zugang ist seit {duration} keinem LinkedIn-Konto zugeordnet. Bitte prüfe deine Profiladresse.',
    sync_stale: 'Die letzte Aktualisierung liegt {duration} zurück.',
    data_gap: 'In deinen Daten gibt es eine Lücke. {reason}',
    processing_errors: 'Beim Lesen der LinkedIn-Daten gab es Fehler (Anzahl: {count}).',
    identity_review: 'Bitte bestätige, ob zwei Einträge dieselbe Person sind.',
    self_url_unverified: 'Deine Profiladresse ist noch nicht bestätigt.',
    privacy_request_due: 'Eine Datenschutzanfrage muss {when} beantwortet sein.',
    privacy_request_overdue: 'Bei einer Datenschutzanfrage ist die Frist überschritten.',
    art14_due: 'Ein Datenschutzhinweis an eine Person ist fällig.',
    erasure_pending: 'Eine Löschung wartet seit {duration} auf die Ausführung.',
    restore_applied: 'Eine Sicherung wurde eingespielt. Bitte prüfe deine Daten.',
    event_delivery_failed: 'Eine Benachrichtigung konnte nicht zugestellt werden.',
    today: 'heute',
    inDuration: 'in {duration}',
    overdue: 'überfällig',
  },

  roles: {
    stewardOnly:
      'Das dürfen nur Datenverantwortliche. Datenverantwortlich ist, wer sich als Erstes in Socialfy angemeldet hat. Weitere Personen ernennt, wer Socialfy auf dem Server betreibt: Bitte diese Person darum.',
  },

  problem: {
    unauthorized: 'Du bist nicht angemeldet. Bitte melde dich an.',
    forbidden: 'Dafür hast du keine Berechtigung.',
    not_found: 'Das gibt es nicht mehr oder nicht für dich.',
    validation: 'Die Eingabe passt nicht. Bitte prüfe sie.',
    conflict: 'Das hat sich gerade geändert. Lade neu und versuche es noch einmal.',
    demo_read_only: 'Im Demo-Modus nicht möglich.',
    rate_limited: 'Das ging zu oft in kurzer Zeit. Bitte warte kurz.',
    gone: 'Das ist nicht mehr verfügbar.',
    unprocessable: 'Das können wir so nicht verarbeiten.',
    unavailable: 'Der Dienst ist gerade nicht erreichbar. Versuche es gleich noch einmal.',
    action_disabled: 'Diese Aktion ist in den Einstellungen ausgeschaltet.',
    internal: 'Das hat nicht geklappt. Versuche es gleich noch einmal.',
    payload_too_large: 'Die Eingabe ist zu lang.',
    limitReached: 'Das Limit ist erreicht. Ab {time} geht es wieder.',
    limitReachedLater: 'Das Limit ist erreicht. Versuche es später noch einmal.',
    contract: 'Die Antwort hatte ein unerwartetes Format.',
    offline: 'Keine Verbindung zum Server.',
    csrf: 'Deine Sitzung war nicht mehr aktuell. Bitte versuche es noch einmal.',
  },

  format: {
    justNow: 'gerade eben',
  },
} as const;

export type Dictionary = typeof de;
